import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { bookingApi, type PickupLocationCabinet } from '@/services/booking/bookingApi';
import { lockerService } from '@/services/locker/lockerService';
import { organizationsService } from '@/services/organizations/organizationsService';
import vehiclesService, { type Vehicle } from '@/services/vehicles/vehiclesService';
import type { VehiclePickupPlacement } from '@/types/vehicle';
import type { Key, VehicleKeyAssignment } from '@/types/locker';
import { BaseModal } from '@/components/common/BaseModal';
import ConnectVehicleKeyCabinets, { type CabinetKeyChoice } from '@/components/fleet/ConnectVehicleKeyCabinets';
import toast from 'react-hot-toast';

interface Props {
  vehicle: Vehicle;
  onClose: () => void;
  onSuccess: () => void;
}

interface LocationOption {
  id: string;
  name: string;
}

type SelectedKey = CabinetKeyChoice;

interface CabinetKeyLink {
  linked?: boolean;
  keyId?: string;
  keyName?: string | null;
  keyNumber?: string | null;
  homeCabinetId?: string | null;
}

function apiMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error) return String(data.error);
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function pickExpanded(list: PickupLocationCabinet[], homeCabinetId: string | null): string | null {
  if (homeCabinetId && list.some((cabinet) => cabinet.cabinet_id === homeCabinetId)) {
    return homeCabinetId;
  }
  if (list.length === 1) return list[0].cabinet_id;
  return null;
}

function selectionForCabinets(
  list: PickupLocationCabinet[],
  current: SelectedKey | null,
  linked: SelectedKey | null
): SelectedKey | null {
  const ids = new Set(list.map((cabinet) => cabinet.cabinet_id));
  if (current && ids.has(current.cabinetId)) return current;
  if (linked && ids.has(linked.cabinetId)) return linked;
  return null;
}

export default function ConnectVehicleKeyModal({ vehicle, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const vehicleId = String(vehicle.id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placements, setPlacements] = useState<VehiclePickupPlacement[]>([]);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [cabinetsByLocation, setCabinetsByLocation] = useState<Record<string, PickupLocationCabinet[]>>({});
  const [cabinets, setCabinets] = useState<PickupLocationCabinet[]>([]);
  const [cabinetsLoading, setCabinetsLoading] = useState(false);
  const [expandedCabinetId, setExpandedCabinetId] = useState<string | null>(null);
  const [keysByCabinet, setKeysByCabinet] = useState<Record<string, Key[]>>({});
  const [keysLoadingId, setKeysLoadingId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<VehicleKeyAssignment[]>([]);
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
  const [linkedKey, setLinkedKey] = useState<SelectedKey | null>(null);
  const [selected, setSelected] = useState<SelectedKey | null>(null);
  const [homeCabinetId, setHomeCabinetId] = useState<string | null>(null);

  const applyCabinets = useCallback((
    locationId: string,
    list: PickupLocationCabinet[],
    homeId: string | null,
    linked: SelectedKey | null
  ) => {
    setCabinetsByLocation((prev) => ({ ...prev, [locationId]: list }));
    setCabinets(list);
    setExpandedCabinetId(pickExpanded(list, homeId));
    setSelected((current) => selectionForCabinets(list, current, linked));
  }, []);

  const loadCabinets = useCallback(async (
    locationId: string,
    homeId: string | null,
    linked: SelectedKey | null
  ) => {
    setCabinetsLoading(true);
    try {
      const list = await bookingApi.getPickupLocationCabinets(locationId);
      applyCabinets(locationId, list, homeId, linked);
    } catch (loadError: unknown) {
      setError(apiMessage(loadError, t('fleet.connectKeyModal.loadError')));
      setCabinets([]);
    } finally {
      setCabinetsLoading(false);
    }
  }, [applyCabinets, t]);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      setLoading(true);
      setError(null);
      try {
        const [placementRows, presenceResult, assignmentResult] = await Promise.all([
          vehiclesService.getVehiclePickupPlacements(vehicleId),
          api.get<{ success: boolean; data: CabinetKeyLink }>(`/api/vehicles/${vehicleId}/cabinet-key`)
            .then((response) => response.data.data)
            .catch(() => ({ linked: false }) as CabinetKeyLink),
          lockerService.getVehicleKeyAssignments()
            .then((rows) => ({ ok: true as const, rows }))
            .catch((assignmentError: unknown) => ({ ok: false as const, error: assignmentError })),
        ]);
        if (cancelled) return;

        setPlacements(placementRows);
        if (assignmentResult.ok) {
          setAssignments(assignmentResult.rows);
          setAssignmentsLoaded(true);
        } else {
          setAssignmentsLoaded(false);
          setError(apiMessage(assignmentResult.error, t('fleet.connectKeyModal.loadError')));
        }

        const homeId = presenceResult.homeCabinetId ? String(presenceResult.homeCabinetId) : null;
        setHomeCabinetId(homeId);
        const linked = presenceResult.linked && presenceResult.keyId && presenceResult.keyNumber && homeId
          ? {
              keyId: String(presenceResult.keyId),
              keyName: String(presenceResult.keyName ?? ''),
              cabinetId: homeId,
              keywinKeyId: String(presenceResult.keyNumber),
            }
          : null;
        setLinkedKey(linked);
        setSelected(linked);

        if (placementRows.length === 0) {
          const topNodeId = await organizationsService.getTopNodeId(vehicle.organization_id).catch(() => null);
          if (cancelled) return;
          const locations = await bookingApi.getPickupLocations(
            false,
            false,
            true,
            undefined,
            topNodeId ?? undefined
          );
          if (cancelled) return;
          setLocationOptions(
            locations
              .filter((location) => location.isActive !== false)
              .map((location) => ({ id: String(location.id), name: location.name }))
          );
          return;
        }

        if (placementRows.length === 1) {
          const locationId = placementRows[0].pickupLocationId;
          setSelectedLocationId(locationId);
          const list = await bookingApi.getPickupLocationCabinets(locationId);
          if (cancelled) return;
          applyCabinets(locationId, list, homeId, linked);
          return;
        }

        const maps = await Promise.all(placementRows.map(async (placement) => ({
          id: placement.pickupLocationId,
          cabinets: await bookingApi.getPickupLocationCabinets(placement.pickupLocationId).catch(() => []),
        })));
        if (cancelled) return;
        const match = homeId
          ? maps.find((entry) => entry.cabinets.some((cabinet) => cabinet.cabinet_id === homeId))
          : undefined;
        const chosen = match?.id ?? placementRows[0].pickupLocationId;
        setCabinetsByLocation(Object.fromEntries(maps.map((entry) => [entry.id, entry.cabinets])));
        setSelectedLocationId(chosen);
        const chosenCabinets = maps.find((entry) => entry.id === chosen)?.cabinets ?? [];
        setCabinets(chosenCabinets);
        setExpandedCabinetId(pickExpanded(chosenCabinets, homeId));
        setSelected(selectionForCabinets(chosenCabinets, linked, linked));
      } catch (loadError: unknown) {
        if (!cancelled) setError(apiMessage(loadError, t('fleet.connectKeyModal.loadError')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void start();
    return () => {
      cancelled = true;
    };
  }, [applyCabinets, t, vehicle.organization_id, vehicleId]);

  useEffect(() => {
    if (!expandedCabinetId || keysByCabinet[expandedCabinetId]) return;
    let cancelled = false;
    setKeysLoadingId(expandedCabinetId);
    lockerService.getKeysInCabinet(expandedCabinetId)
      .then((keys) => {
        if (cancelled) return;
        setKeysByCabinet((prev) => ({ ...prev, [expandedCabinetId]: keys }));
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setKeysByCabinet((prev) => (
          prev[expandedCabinetId] ? prev : { ...prev, [expandedCabinetId]: [] }
        ));
        setError(apiMessage(loadError, t('fleet.connectKeyModal.loadError')));
      })
      .finally(() => {
        if (!cancelled) setKeysLoadingId((current) => (current === expandedCabinetId ? null : current));
      });
    return () => {
      cancelled = true;
    };
  }, [expandedCabinetId, keysByCabinet, t]);

  const chooseLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
    if (!locationId) {
      setCabinets([]);
      setExpandedCabinetId(null);
      return;
    }
    const cached = cabinetsByLocation[locationId];
    if (cached) {
      setCabinets(cached);
      setExpandedCabinetId(pickExpanded(cached, homeCabinetId));
      setSelected((current) => selectionForCabinets(cached, current, linkedKey));
      return;
    }
    if (locationId) void loadCabinets(locationId, homeCabinetId, linkedKey);
  };

  const save = async () => {
    if (!selected || !selectedLocationId || saving) return;
    setSaving(true);
    try {
      const alreadyLinked = placements.some((placement) => placement.pickupLocationId === selectedLocationId);
      if (!alreadyLinked) {
        const vehicleNumber = Number(vehicleId);
        if (!Number.isFinite(vehicleNumber)) {
          throw new Error(t('fleet.connectKeyModal.saveError'));
        }
        await bookingApi.addVehicleToPickupLocation(selectedLocationId, { vehicleId: vehicleNumber });
        setPlacements((prev) => [
          ...prev,
          {
            pickupLocationVehicleId: '',
            pickupLocationId: selectedLocationId,
            pickupLocationName: locationOptions.find((location) => location.id === selectedLocationId)?.name ?? '',
            pickupLocationDescription: null,
            displayName: null,
            isAvailable: true,
            resourceTypeName: null,
          },
        ]);
      }
      if (!linkedKey || linkedKey.keyId !== selected.keyId) {
        await lockerService.assignVehicleToKey(
          vehicleId,
          selected.keyId,
          selected.keyName,
          selected.cabinetId,
          undefined,
          selected.keywinKeyId
        );
        toast.success(t('fleet.connectKeyModal.saved', { reg: vehicle.registration_number }));
      }
      onSuccess();
    } catch (saveError: unknown) {
      toast.error(apiMessage(saveError, t('fleet.connectKeyModal.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const title = linkedKey
    ? t('fleet.connectKeyModal.editTitle', { reg: vehicle.registration_number })
    : t('fleet.connectKeyModal.title', { reg: vehicle.registration_number });
  const canSave = Boolean(selected && selectedLocationId) && !saving && !loading;

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={title}
      size="screen"
      loading={saving}
      footer={(
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-textSecondary truncate">
            {selected
              ? t('fleet.connectKeyModal.selectedKey', { name: selected.keyName || selected.keywinKeyId })
              : t('fleet.connectKeyModal.selectKey')}
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-textSecondary hover:bg-bgHover"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => { void save(); }}
              disabled={!canSave}
              className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}
    >
      {error && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {loading ? (
        <div className="animate-pulse h-28 bg-bgElevated rounded-lg" />
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1" htmlFor="connect-key-location">
              {t('fleet.connectKeyModal.pickupLocation')}
            </label>
            {placements.length === 1 ? (
              <p className="text-sm text-textPrimary">{placements[0].pickupLocationName}</p>
            ) : (
              <select
                id="connect-key-location"
                value={selectedLocationId}
                onChange={(event) => chooseLocation(event.target.value)}
                className="w-full max-w-md px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
              >
                {placements.length === 0 && (
                  <option value="">{t('fleet.connectKeyModal.selectPickupLocation')}</option>
                )}
                {(placements.length > 0
                  ? placements.map((placement) => ({
                      id: placement.pickupLocationId,
                      name: placement.pickupLocationName,
                    }))
                  : locationOptions
                ).map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            )}
            {placements.length === 0 && locationOptions.length === 0 && (
              <p className="mt-1 text-sm text-textTertiary">{t('fleet.connectKeyModal.noPickupLocations')}</p>
            )}
          </div>

          {selectedLocationId && cabinetsLoading && (
            <div className="animate-pulse h-16 bg-bgElevated rounded-lg" />
          )}
          {selectedLocationId && !cabinetsLoading && cabinets.length === 0 && (
            <p className="text-sm text-textSecondary">{t('fleet.connectKeyModal.noCabinets')}</p>
          )}

          <ConnectVehicleKeyCabinets
            cabinets={cabinets}
            expandedCabinetId={expandedCabinetId}
            onToggle={(cabinetId) => setExpandedCabinetId(expandedCabinetId === cabinetId ? null : cabinetId)}
            keysByCabinet={keysByCabinet}
            keysLoadingId={keysLoadingId}
            assignments={assignments}
            assignmentsLoaded={assignmentsLoaded}
            vehicleId={vehicleId}
            selectedKeyId={selected?.keyId}
            onSelect={setSelected}
          />
        </div>
      )}
    </BaseModal>
  );
}
